/**
 * @file Garde-fou de fluidite : si la frequence d'affichage reste sous un seuil pendant une fenetre complete alors que le
 * post-traitement (bloom) est actif, signale une fois qu'il faut l'alleger pour la session. Pur (aucun DOM / Three), teste.
 * Les frames aberrantes (onglet en arriere-plan, chargement, dt <= 0) remettent la fenetre a zero sans declencher.
 */
export interface FpsGuardOptions {
  /** Seuil (images/s) sous lequel la fenetre est jugee trop lente. */
  minFps: number;
  /** Duree d'une fenetre de mesure (s). */
  window: number;
  /** Temps ignore au demarrage / apres reinitialisation (compilation des shaders, chargement du biome). */
  warmup: number;
  /** Au-dela (s), la frame n'est pas une mesure de fluidite (onglet en arriere-plan, chargement) : la fenetre repart de zero. */
  maxDt: number;
}

export const FPS_GUARD_DEFAULTS: FpsGuardOptions = { minFps: 45, window: 5, warmup: 4, maxDt: 1 };

export class FpsGuard {
  private warm = 0;
  private acc = 0;
  private frames = 0;
  private done = false;

  constructor(private readonly opts: FpsGuardOptions = FPS_GUARD_DEFAULTS) {}

  /** A appeler a chaque frame ou le post-traitement est actif ; renvoie `true` une seule fois, au declenchement. */
  sample(dt: number): boolean {
    if (this.done) return false;
    if (!(dt > 0) || dt > this.opts.maxDt) {
      this.acc = 0;
      this.frames = 0;
      return false;
    }
    if (this.warm < this.opts.warmup) {
      this.warm += dt;
      return false;
    }
    this.acc += dt;
    this.frames++;
    if (this.acc < this.opts.window) return false;
    const fps = this.frames / this.acc;
    this.acc = 0;
    this.frames = 0;
    if (fps >= this.opts.minFps) return false;
    this.done = true;
    return true;
  }

  /** Interrompt la fenetre en cours (post coupe, pause) sans oublier le prechauffage deja ecoule. */
  pause(): void {
    this.acc = 0;
    this.frames = 0;
  }

  get tripped(): boolean {
    return this.done;
  }

  reset(): void {
    this.warm = 0;
    this.acc = 0;
    this.frames = 0;
    this.done = false;
  }
}
